<script setup>
import {computed, reactive, ref, watch} from 'vue'
import { ElMessage } from 'element-plus'
import { createNotification, notificationDetail, updateNotification } from "@/api/notificationChannel";
import { useI18n } from "vue-i18n";

// 引入i18n用于国际化
const { t } = useI18n();
// 表单引用和数据
const ruleFormRef = ref(null)
const ruleForm = ref({
  noticeName: '', // 通知名称
  noticeMode: ['email','phoneMessage'], // 通知方式
  email: {
    emails: [''] // 邮箱地址，默认为一个空字符串
  },
  phoneMessage: ['']
})

// 父组件传入的 `id` 参数
const props = defineProps({
  id: {
    type: [Number, String], // 支持 Number 或 String 类型
  }
})

// 自定义事件 `submitCancel`，用于关闭弹窗
const emit = defineEmits(['submitCancel'])

// 获取通知详情，如果有 `id`，则拉取详情数据
const getDetail = () => {
  if (props.id) {
    notificationDetail(props.id).then(res => {
      if (res && res.data) {
        ruleForm.value = res.data // 将返回的数据赋值给表单
      }
    })
  }
}

// 新增通知时重置表单数据
const addNotification = () => {
  ruleForm.value = {
    noticeName: '',
    noticeMode: ['email','phoneMessage'],
    email: {
      emails: [''] // 重置为一个空的邮箱输入框
    },
    phoneMessage: ['']
  }
}

// 暴露给父组件的方法
defineExpose({ getDetail, addNotification })


const validateEmails = (rule, value, callback) => {
  if (!value || value.length === 0) {
    return callback(new Error(t('notification.emailEmpty')))
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  for (let i = 0; i < value.length; i++) {
    const email = value[i]
    if (!emailPattern.test(email)) {
      return callback(new Error(t('notification.emailFormat')))
    }
  }
  callback()
}

const validatePhoneMessage = (rule, value, callback) => {
  if (!value || value.length === 0) {
    return callback(new Error(t('notification.phoneMessage')))
  }

  const phoneMessagePattern = /^\+?[1-9]\d{1,14}$/
  for (let i = 0; i < value.length; i++) {
    console.log(' = value = ', value)
    const phone = value[i]
    if (!phoneMessagePattern.test(phone)) {
      return callback(new Error(t('notification.phoneMessage')))
    }
  }
  callback()
}


// 表单验证规则
const rules = computed(() => {
  const baseRules = {
    noticeName: [
      { required: true, message: t('notification.nameEmpty'), trigger: 'blur' },
    ],
    noticeMode: [
      { required: true, message: t('notification.typeEmpty'), trigger: 'blur' },
    ],
  };

  if (ruleForm.value.noticeMode?.includes('email')) {
    baseRules['email.emails'] = [
      { required: true, message: t('notification.emailEmpty'), trigger: 'blur' },
      { validator: validateEmails, trigger: ['blur', 'change'] }
    ];
  }

  if (ruleForm.value.noticeMode?.includes('phoneMessage')) {
    baseRules.phoneMessage = [
      { required: true, message: t('notification.phoneMessage'), trigger: 'blur' },
      { validator: validatePhoneMessage, trigger: ['blur', 'change'] }
    ];
  }

  return baseRules;
});

// 新增手机号
const addPhone = () => {
  console.log(' = ruleForm.value.phoneMessage', ruleForm.value.phoneMessage)
  ruleForm.value.phoneMessage.push('') // 向邮箱数组添加一个空字符串
}

// 删除手机号
const removePhone = (index) => {
  if (ruleForm.value.phoneMessage.length > 1) {
    ruleForm.value.phoneMessage.splice(index, 1) // 删除指定索引的邮箱
  } else {
    ruleForm.value.phoneMessage[0] = '' // 保留至少一个输入框
  }
}

// 新增邮箱输入框
const addEmail = () => {
  ruleForm.value.email.emails.push('') // 向邮箱数组添加一个空字符串
}

// 删除邮箱输入框
const removeEmail = (index) => {
  if (ruleForm.value.email.emails.length > 1) {
    ruleForm.value.email.emails.splice(index, 1) // 删除指定索引的邮箱
  } else {
    ruleForm.value.email.emails[0] = '' // 保留至少一个输入框
  }
}

// 提交表单
const submitForm = async (formEl) => {
  if (!formEl) return

  await formEl.validate((valid) => {
    if (valid) {
      // 如果有 `id`，则更新通知；否则创建新通知
      const apiCall = props.id ? updateNotification : createNotification
      apiCall(ruleForm.value).then(() => {
        ElMessage({
          message: props.id ? t('notification.updateSuccessfully') : t('notification.createdSuccessfully'),
          type: 'success',
        })
        emit('submitCancel') // 提交成功后关闭弹窗
      }).catch(err => {
        ElMessage({
          message: err,
          type: 'error',
        })
      })
    } else {
      console.log('表单验证失败')
    }
  })
}

// 重置表单并关闭弹窗
const resetForm = () => {
  emit('submitCancel')
}
</script>

<template>
  <el-form
      ref="ruleFormRef"
      style="max-width: 600px"
      :model="ruleForm"
      :rules="rules"
      label-width="auto"
      status-icon
  >
    <el-form-item :label="$t('notification.notificationChannelName')" prop="noticeName">
      <el-input v-model="ruleForm.noticeName" />
    </el-form-item>

    <el-form-item :label="$t('notification.notificationWay')" prop="noticeMode">
      <el-checkbox-group v-model="ruleForm.noticeMode">
        <el-checkbox value="email" name="type">{{$t('notification.email')}}</el-checkbox>
        <el-checkbox value="phoneMessage" name="type">{{$t('notification.phoneMessage')}}</el-checkbox>
      </el-checkbox-group>
    </el-form-item>

    <el-tabs type="border-card" class="demo-tabs">
      <el-tab-pane>
        <template #label>
          <span class="custom-tabs-label">
            <span>{{$t('notification.emailAddress')}}</span>
          </span>
        </template>
        <el-form-item prop="email.emails" :label="$t('notification.emailAddress')">
          <div v-for="(item, index) in ruleForm.email.emails" :key="index" style="width: 70%;" class="d-flex mt-2">
            <el-input v-model="ruleForm.email.emails[index]" :placeholder="$t('notification.emailAddress')" />
            <el-button type="danger" @click="removeEmail(index)" style="margin-left: 10px;">{{$t('alarm.delete')}}</el-button>
          </div>
          <el-button type="primary" @click="addEmail" style="margin-left: 10px;" class="mt-2">{{$t('alarm.add')}}</el-button>
        </el-form-item>
      </el-tab-pane>
      <el-tab-pane label="Config">
        <template #label>
          <span class="custom-tabs-label">
            <span>{{$t('notification.phoneMessage')}}</span>
          </span>
        </template>
        <el-form-item prop="phoneMessage" :label="$t('notification.phoneMessage')">
          <div v-for="(item, index) in ruleForm.phoneMessage" :key="index" style="width: 70%;" class="d-flex mt-2">
            <el-input v-model="ruleForm.phoneMessage[index]" :placeholder="$t('notification.phoneMessage')" />
            <el-button type="danger" @click="removePhone(index)" style="margin-left: 10px;">{{$t('alarm.delete')}}</el-button>
          </div>
          <el-button v-if="ruleForm.phoneMessage && ruleForm.phoneMessage.length <= 2" type="primary" @click="addPhone" style="margin-left: 10px;" class="mt-2">{{$t('alarm.add')}}</el-button>
        </el-form-item>
      </el-tab-pane>
    </el-tabs>

    <el-form-item class="mt-2">
      <el-button type="primary" @click="submitForm(ruleFormRef)">{{$t('group.submit')}}</el-button>
      <el-button @click="resetForm">{{$t('group.cancel')}}</el-button>
    </el-form-item>
  </el-form>
</template>

<style scoped>
</style>
